/**
 * SKE-5376. One read for every surface that shows phone publishing on an
 * Instagram API account: the post cards and the account page section.
 *
 * A context rather than a hook in each card, for the same reason as
 * `features/instagram-campaign/cutover-marker.tsx`: cards render by the
 * hundred and `useCampaignStatus` throws without a QueryClientProvider. A
 * tree without the provider, or with no cutover date known, gets the inert
 * default: no card warnings, as the cutover marker does.
 */
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { cn } from "../../lib/utils";
import { getUser } from "../../selectors/commonSelectors";
import { getAccounts } from "../../selectors/skedCoreSelectors";
import type { AccountType } from "../../shared/types/accounts";
import { isInstagramPhoneStateIgnored } from "../../entities/accounts/libs/get-account-statuses";
import { useInstagramApiPublishingOn } from "../../utils/feature-toggle-utils";
import { formatCutover } from "../instagram-campaign/campaign-registers";
import { useCampaignStatus } from "../instagram-campaign/use-campaign-status";
import {
  PHONE_PUBLISHING_COPY,
  isPhoneLoginBroken,
  phoneBlockers,
  phonePublishingSectionHref,
  type PhoneRoutedPostShape,
} from "./phone-publishing";

export interface PhonePublishingState {
  /** `IG_RECONNECT_CUTOVER_AT` has passed: nothing publishes from the phone. */
  isPastCutover: boolean;
  /** The cutover date as the cutover marker formats it, or null when none is known. */
  cutoverLabel: string | null;
  /** The first account this post needs a working phone login for, or null. */
  accountNeedingPhoneLogin: (
    post: PhoneRoutedPostShape,
    accountIds: Array<string | null | undefined>
  ) => string | null;
}

const INERT: PhonePublishingState = {
  isPastCutover: false,
  cutoverLabel: null,
  accountNeedingPhoneLogin: () => null,
};

export const PhonePublishingContext =
  createContext<PhonePublishingState>(INERT);

export function usePhonePublishing(): PhonePublishingState {
  return useContext(PhonePublishingContext);
}

export function InstagramPhonePublishingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { status } = useCampaignStatus();
  const apiPublishingOn = useInstagramApiPublishingOn();
  const accounts = useSelector(getAccounts) as AccountType[];
  const timeZone = useSelector(getUser)?.timezone ?? null;

  const value = useMemo<PhonePublishingState>(() => {
    if (status?.campaign?.phase === "past") {
      return { ...INERT, isPastCutover: true };
    }
    const cutoverLabel =
      formatCutover(status?.campaign?.cutoverAt, timeZone) || null;
    if (!apiPublishingOn || !cutoverLabel) return INERT;

    const atRisk = new Map(
      accounts
        .filter(
          (account) =>
            isInstagramPhoneStateIgnored(account, apiPublishingOn) &&
            isPhoneLoginBroken(account)
        )
        .map((account) => [String(account._id), account])
    );
    if (atRisk.size === 0) return { ...INERT, cutoverLabel };

    return {
      isPastCutover: false,
      cutoverLabel,
      accountNeedingPhoneLogin: (post, accountIds) => {
        for (const id of accountIds) {
          const account = id ? atRisk.get(String(id)) : undefined;
          if (account && phoneBlockers(post, account).length > 0) {
            return String(account._id);
          }
        }
        return null;
      },
    };
  }, [status, apiPublishingOn, accounts, timeZone]);

  return (
    <PhonePublishingContext.Provider value={value}>
      {children}
    </PhonePublishingContext.Provider>
  );
}

/**
 * The amber line on a post card, with a link to the account's section. The
 * `tile` size is for a calendar tile: one small line that truncates the
 * message but always keeps the Fix link, so the tile never grows.
 */
export function PhoneLoginCardWarning({
  post,
  accountIds,
  size = "card",
  className,
}: {
  post: PhoneRoutedPostShape;
  accountIds: Array<string | null | undefined>;
  size?: "card" | "tile";
  className?: string;
}) {
  const { accountNeedingPhoneLogin } = usePhonePublishing();
  const accountId = accountNeedingPhoneLogin(post, accountIds);
  if (!accountId) return null;
  const tile = size === "tile";
  return (
    <p
      data-testid="phone-login-card-warning"
      title={tile ? PHONE_PUBLISHING_COPY.cardWarning : undefined}
      className={cn(
        "m-0 flex items-center font-inter font-medium text-yellow-700",
        tile
          ? "min-w-0 gap-1 whitespace-nowrap text-[10px] leading-[12px]"
          : "gap-1.5 text-sm leading-5",
        className
      )}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <i
        aria-hidden="true"
        className={cn(
          "fa-solid fa-triangle-exclamation",
          tile ? "shrink-0 text-[9px]" : "text-xs"
        )}
      />
      <span className={tile ? "min-w-0 truncate" : undefined}>
        {PHONE_PUBLISHING_COPY.cardWarning}
      </span>
      <Link
        to={phonePublishingSectionHref(accountId)}
        className="shrink-0 font-semibold text-yellow-700 underline underline-offset-2 hover:text-yellow-800"
      >
        {PHONE_PUBLISHING_COPY.cardFix}
      </Link>
    </p>
  );
}
